import { EntitlementSlugEnum } from '@electricjs/core_entity-client';

import { useAppSelector } from '@/redux/store';

export const useOrganizationHasEntitlement = (
  slug: EntitlementSlugEnum
): boolean => {
  const entitlementSlugs =
    useAppSelector(state => state.loggedUser?.organizationEntitlementSlugs) ||
    [];

  return entitlementSlugs.includes(slug);
};

// Utility type to convert snake_case to camelCase
type SnakeToCamelCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<SnakeToCamelCase<U>>}`
  : S;

// Utility type to create the entitlement property names
type EntitlementPropertyName<T extends string> =
  `has${Capitalize<SnakeToCamelCase<T>>}Entitlement`;

// Create the return type for all entitlements
type AllEntitlementsResult = {
  [K in keyof typeof EntitlementSlugEnum as EntitlementPropertyName<
    (typeof EntitlementSlugEnum)[K]
  >]: boolean;
};

export const useOrganizationHasEntitlements = (): AllEntitlementsResult => {
  const entitlementSlugs =
    useAppSelector(state => state.loggedUser?.organizationEntitlementSlugs) ||
    [];
  const entitlements = new Set(entitlementSlugs);

  const result: { [k: string]: boolean } = {};

  // Iterate through all enum keys
  for (const value of Object.values(EntitlementSlugEnum)) {
    const hasEntitlement = entitlements.has(value);

    // Convert snake_case to camelCase and create property name
    const camelCase = value.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase()
    );
    const propertyName =
      `has${camelCase.charAt(0).toUpperCase() + camelCase.slice(1)}Entitlement` as keyof AllEntitlementsResult;

    result[propertyName] = hasEntitlement;
  }

  return result as AllEntitlementsResult;
};
